import {
	MarketDataFeed,
	TradeSubscriberSubscription,
	Serializer,
	MarketSymbol,
} from '@velocity-exchange/common';
import { useContext, useEffect, useRef } from 'react';
import Env from '../../environmentVariables/EnvironmentVariables';
import { DriftBlockchainEventSubjectContext } from '../../providers/driftEvents/driftEventSubjectProvider';
import useEventsSubscriptionStateStore from '../../stores/useEventsSubscriptionStateStore';
import { dlog } from '../../dev';
import useDriftStore from '../../stores/DriftStore/useDriftStore';

/**
 * SEE ui/src/hooks/driftEvents/README.md for explanation of how this hook links with rest of event syncing
 */

const useSyncRecentTradesWithDataApiServer = () => {
	// Variables
	const currentMarket = useDriftStore((s) => s.selectedMarket.current);

	const currentMarketSymbol = currentMarket?.uiSymbols.marketSymbol;

	const isEnabled = useEventsSubscriptionStateStore(
		(s) => s.globalEventsState.subscriptionType === 'data-api-trade-publisher'
	);

	const globalFillEventSubject = useContext(
		DriftBlockchainEventSubjectContext
	).globalFillEventSubject;

	const subscriptionRef = useRef<TradeSubscriberSubscription>(undefined);

	const setUpSubscription = () => {
		dlog(
			`optimised_event_subscriptions::data-api-trade-publisher`,
			`setting_up_subscription`,
			`trades::${Env.dataApiEnvToUse.key}::${currentMarketSymbol}`
		);

		if (subscriptionRef.current) {
			throw new Error(
				'Tried to make new data api trade publisher subscription while one already exists'
			);
		}

		const subscription = MarketDataFeed.subscribe({
			type: 'trades',
			env: Env.dataApiEnvToUse,
			marketSymbol: currentMarketSymbol as MarketSymbol,
		});

		subscriptionRef.current = subscription;

		subscription.observable.subscribe({
			next: (trades) => {
				dlog(
					`optimised_event_subscriptions::data-api-trade-publisher`,
					`received_trades (${trades.length})`
				);

				const fillEvents = trades.map(
					Serializer.Deserialize.DataApiOrderActionRecord
				);

				fillEvents.forEach((fillEvent) => {
					globalFillEventSubject?.next(fillEvent);
				});
			},
			error: (error) => {
				console.error(error);
			},
		});
	};

	const cleanupSubscription = () => {
		dlog(
			`optimised_event_subscriptions::data-api-trade-publisher`,
			`tearing_down_subscription`
		);

		if (subscriptionRef.current) {
			MarketDataFeed.unsubscribe(subscriptionRef.current.id);
			subscriptionRef.current = undefined;
		}
	};

	const subscriptionUniquenessKey = `${Env.dataApiEnvToUse.key}::${currentMarketSymbol}`;

	useEffect(() => {
		if (!globalFillEventSubject) return;
		if (!currentMarketSymbol) return;
		if (!isEnabled) return;

		setUpSubscription();

		return () => {
			cleanupSubscription();
		};
	}, [subscriptionUniquenessKey, globalFillEventSubject, isEnabled]);
};

export default useSyncRecentTradesWithDataApiServer;
